export const MANUAL_LEAD_STATUSES = [
  'unattended',
  'qualified',
  'allocated',
  'in-progress',
  'not-interested',
  'wrong-number',
  'invalid-lead',
  'wrong-number-invalid-lead',
  'positive-send-details',
  'no-answer-call-later',
  'entry-not-met',
  'transfer-to-another-programme',
  'next-intake'
];

export const AUTO_LEAD_STATUSES = [
  'application-link-shared',
  'application-submitted',
  'payment-verification-pending',
  'payment-rejected',
  'document-pending',
  'documents-approved',
  'application-rejected',
  'application-approved'
];

export const LEAD_STATUS_VALUES = [...MANUAL_LEAD_STATUSES, ...AUTO_LEAD_STATUSES];

export const TERMINAL_LEAD_STATUSES = ['application-approved', 'registered'];

export const LEAD_STATUS_LABELS = {
  unattended: 'Unattended',
  qualified: 'Qualified',
  allocated: 'Allocated',
  'in-progress': 'In Progress',
  'not-interested': 'Not Interested',
  'wrong-number': 'Wrong Number',
  'invalid-lead': 'Invalid Lead',
  'wrong-number-invalid-lead': 'Wrong Number / Invalid Lead',
  'no-answer-call-later': 'No Answer / Call Later',
  'entry-not-met': 'Entry Not Met',
  'transfer-to-another-programme': 'Transfer to Another Programme',
  'next-intake': 'Next Intake',
  'positive-send-details': 'Positive / Send Details',
  'application-link-shared': 'Application Link Shared',
  'application-submitted': 'Application Submitted',
  'payment-verification-pending': 'Payment Verification Pending',
  'payment-rejected': 'Payment Rejected',
  'document-pending': 'Documents Pending',
  'documents-approved': 'Documents Approved',
  'application-rejected': 'Application Rejected',
  'application-approved': 'All Approved',
  registered: 'Registered'
};

export const LEAD_STATUS_GROUPS = {
  inProgress: [
    'in-progress',
    'application-link-shared',
    'application-submitted',
    'payment-verification-pending',
    'payment-rejected'
  ],
  registered: [
    'document-pending',
    'documents-approved',
    'application-rejected',
    'application-approved'
  ]
};

export const GROUP_DISPLAY_LABELS = {
  inProgress: 'In Progress',
  registered: 'Registered'
};

export const LEAD_STATUS_BADGE_COLORS = {
  unattended: 'bg-blue-100 text-blue-800 border-blue-200',
  allocated: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  qualified: 'bg-green-100 text-green-800 border-green-200',
  'in-progress': 'bg-orange-100 text-orange-800 border-orange-200',
  'not-interested': 'bg-red-100 text-red-800 border-red-200',
  'wrong-number': 'bg-red-100 text-red-800 border-red-200',
  'invalid-lead': 'bg-red-100 text-red-800 border-red-200',
  'wrong-number-invalid-lead': 'bg-red-100 text-red-800 border-red-200',
  'no-answer-call-later': 'bg-amber-100 text-amber-800 border-amber-200',
  'entry-not-met': 'bg-pink-100 text-pink-800 border-pink-200',
  'transfer-to-another-programme': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'next-intake': 'bg-violet-100 text-violet-800 border-violet-200',
  'positive-send-details': 'bg-teal-100 text-teal-800 border-teal-200',
  'application-link-shared': 'bg-sky-100 text-sky-800 border-sky-200',
  'application-submitted': 'bg-blue-100 text-blue-800 border-blue-200',
  'payment-verification-pending': 'bg-orange-100 text-orange-800 border-orange-200',
  'payment-rejected': 'bg-red-100 text-red-800 border-red-200',
  'document-pending': 'bg-amber-100 text-amber-800 border-amber-200',
  'documents-approved': 'bg-lime-100 text-lime-800 border-lime-200',
  'application-rejected': 'bg-rose-100 text-rose-800 border-rose-200',
  'application-approved': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  registered: 'bg-green-100 text-green-800 border-green-200'
};

const normalizeLeadStatus = (status) => String(status || '').trim().toLowerCase();

export const isTransferredToStatus = (status) => {
  const norm = normalizeLeadStatus(status);
  if (norm === 'transfer-to-another-programme' || norm === 'transfer to another programme') {
    return false;
  }
  return norm.startsWith('transferred to ') || norm.startsWith('transfer to ');
};

export const isAutoLeadStatus = (status) => AUTO_LEAD_STATUSES.includes(normalizeLeadStatus(status));

export const isTerminalLeadStatus = (status) => TERMINAL_LEAD_STATUSES.includes(normalizeLeadStatus(status));

export const isManualLeadStatus = (status) => {
  const norm = normalizeLeadStatus(status);
  return MANUAL_LEAD_STATUSES.includes(norm) || isTransferredToStatus(norm);
};

export const isValidLeadStatus = (status) => {
  if (!status || typeof status !== 'string') return false;
  const norm = normalizeLeadStatus(status);
  return LEAD_STATUS_VALUES.includes(norm) || isTransferredToStatus(norm) || norm === 'registered';
};

export const getLeadStatusLabel = (status, { grouped = false } = {}) => {
  if (!status) return '';
  const raw = String(status).trim();
  const key = normalizeLeadStatus(raw);
  if (grouped) {
    if (LEAD_STATUS_GROUPS.inProgress.includes(key)) return GROUP_DISPLAY_LABELS.inProgress;
    if (LEAD_STATUS_GROUPS.registered.includes(key)) return GROUP_DISPLAY_LABELS.registered;
  }
  if (LEAD_STATUS_LABELS[key]) return LEAD_STATUS_LABELS[key];
  if (isTransferredToStatus(key)) {
    const match = raw.match(/^(?:transferred|transfer)\s+to\s+(.*)$/i);
    if (match && match[1]) {
      const loc = match[1].trim();
      return `Transferred to ${loc}`;
    }
  }
  return key.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export const getLeadStatusBadgeColor = (status) => {
  const key = normalizeLeadStatus(status);
  if (isTransferredToStatus(key)) {
    return 'bg-cyan-100 text-cyan-800 border-cyan-200';
  }
  return LEAD_STATUS_BADGE_COLORS[key] || 'bg-gray-100 text-gray-800 border-gray-200';
};

export const getLeadStatusGroup = (status) => {
  const key = normalizeLeadStatus(status);
  if (LEAD_STATUS_GROUPS.inProgress.includes(key)) return 'inProgress';
  if (LEAD_STATUS_GROUPS.registered.includes(key)) return 'registered';
  return null;
};

export const aggregateStatusCountsByGroup = (byStatus = {}) => {
  const manualEntries = {};
  let inProgressCount = 0;
  let registeredCount = 0;

  Object.entries(byStatus).forEach(([status, count]) => {
    const key = normalizeLeadStatus(status);
    const group = getLeadStatusGroup(key);
    if (group === 'inProgress') {
      inProgressCount += count;
    } else if (group === 'registered') {
      registeredCount += count;
    } else if (MANUAL_LEAD_STATUSES.includes(key) || isManualLeadStatus(key)) {
      manualEntries[status] = (manualEntries[status] || 0) + count;
    } else if (key === 'registered') {
      registeredCount += count;
    }
  });

  const result = { ...manualEntries };
  if (inProgressCount > 0) result['in-progress'] = inProgressCount;
  if (registeredCount > 0) result.registered = registeredCount;
  return result;
};

export const isInApplicationPipeline = (status) => isAutoLeadStatus(status);

export const isLeadStatusLockedForManualEdit = (status) =>
  isAutoLeadStatus(status) || isTerminalLeadStatus(status);

export const isLeadConverted = (lead) => {
  if (!lead) return false;
  if (lead.convertedToCustomer) return true;
  const status = normalizeLeadStatus(lead.leadStatus);
  return status === 'registered' || status === 'application-approved';
};

export const formatStatusLabel = (status, options) => getLeadStatusLabel(status, options);

/**
 * Builds dynamic "Transferred to {Location}" statuses from locations in administration:
 * - Filters for active locations (status === 'active' && settings.isActive === true && valid type)
 * - Excludes locations with Location Type 'Franchise Center' or 'Warehouse'
 * - Uses normalized City value by default ('Transferred to ' prefix is hardcoded)
 * - Normalizes postal districts (e.g. 'Colombo 08' -> 'Colombo')
 * - Deduplicates locations in the same city (prioritizes branches over offices)
 * - If City is not available, uses cleaned Location Name
 */
export const buildDynamicLocationStatuses = (locations = []) => {
  if (!Array.isArray(locations)) return [];

  const normalizeCity = (cityStr) => {
    if (!cityStr) return '';
    // Strip trailing postal districts/numbers, e.g. "Colombo 08" -> "Colombo", "Colombo 8" -> "Colombo"
    return String(cityStr).replace(/[\s-]+0?\d+$/, '').trim();
  };

  // 1. Filter: active, settings.isActive === true, valid type, and NOT Franchise Center / Warehouse
  const eligible = locations.filter((loc) => {
    if (!loc) return false;
    const status = String(loc.status || '').trim().toLowerCase();
    if (status !== 'active') return false;
    if (!loc.settings || loc.settings.isActive !== true) return false;
    if (!loc.type) return false;

    const type = String(loc.type || '').trim().toLowerCase();
    const operatingMode = String(loc.operatingMode || '').trim().toLowerCase();
    if (type === 'franchise_center' || type === 'franchise center' || operatingMode === 'franchise') {
      return false;
    }
    if (type === 'warehouse') {
      return false;
    }
    return true;
  });

  // Sort so branch takes precedence over office when deduplicating by city
  eligible.sort((a, b) => {
    const typeA = String(a.type || '').toLowerCase();
    const typeB = String(b.type || '').toLowerCase();
    if (typeA === 'branch' && typeB !== 'branch') return -1;
    if (typeB === 'branch' && typeA !== 'branch') return 1;
    return 0;
  });

  // 2. Form dynamic status objects with deduplication
  const statuses = [];
  const seenKeys = new Set();

  eligible.forEach((loc) => {
    const name = String(loc.name || '').trim();
    const rawCity = typeof loc.address === 'object' && loc.address?.city
      ? loc.address.city
      : (typeof loc.city === 'string' ? loc.city : '');
    const city = normalizeCity(rawCity);

    // Use normalized city if present; otherwise clean location name
    let locationLabel = '';
    if (city) {
      locationLabel = city;
    } else {
      locationLabel = name
        .replace(/^SLIM\s+(?:Campus|Business School)\s*[-–]\s*/i, '')
        .replace(/^SBS\s*[-–]\s*/i, '')
        .trim() || name;
    }

    if (!locationLabel) return;

    const statusValue = `Transferred to ${locationLabel}`;
    const dedupeKey = statusValue.toLowerCase();

    if (!seenKeys.has(dedupeKey)) {
      seenKeys.add(dedupeKey);
      statuses.push({
        value: statusValue,
        label: statusValue,
        locationId: loc._id || loc.id,
        locationName: name,
        city: city || null
      });
    }
  });

  return statuses.sort((a, b) => a.label.localeCompare(b.label));
};
